// MARKET INTEL — four live trader widgets mounted below the six Tool cards
// on /tools. Card styling mirrors the existing FLEXX aesthetic (dark #060606 +
// gold #C9A227 left-accent, minimal chrome).
//
// 1. Crypto Fear & Greed Index (alternative.me public API, no key).
// 2. Gold (XAUUSD) Daily Pivots — auto-fetched via backend proxy from
//    TwelveData. Admin can manually override via small dialog if API is down.
// 3. DXY Direction Indicator (auto-fetched from Yahoo DX-Y.NYB, 30-min cache).
// 4. Today's Economic Calendar — auto-fetched via backend proxy from FMP.
//    High-impact events render as a red banner; medium-impact is collapsed
//    under "+ More events"; low-impact is hidden.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import Portal from "../Portal";
import AxelMacroBrief from "./AxelMacroBrief";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
function authHeaders() {
  const token = localStorage.getItem("flexx_access_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const T = {
  bg: "#060606", card: "#0d1218", border: "#1C1C1C",
  gold: "#C9A227", text: "#EDE8DC", mute: "#AAAAAA",
  green: "#22C55E", red: "#FF4444", teal: "#14B8A6", orange: "#FF8C42", yellow: "#FFB347",
};

export default function MarketIntel() {
  return (
    <div data-testid="market-intel-section" style={{ marginTop: 28 }}>
      <div style={{ fontFamily: "monospace", color: T.gold, fontSize: 11, letterSpacing: 2.5, marginBottom: 6 }}>
        LIVE MARKET PULSE
      </div>
      <h2 data-testid="market-intel-title" style={{
        margin: "4px 0 14px", fontSize: 26, fontFamily: "'Bebas Neue','Oswald',sans-serif",
        letterSpacing: 0.5, color: T.text,
      }}>MARKET INTEL</h2>

      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
        gap: 14,
      }}>
        <FearGreedCard />
        <GoldPivotsCard />
        <DxyDirectionCard />
        <EconomicCalendarCard />
      </div>

      <AxelMacroBrief />
    </div>
  );
}

// Shared card chrome
const cardStyle = {
  background: T.card, border: `1px solid ${T.border}`,
  borderLeft: `3px solid ${T.gold}`,
  borderRadius: 12, padding: 16,
};
const headerLabel = {
  fontFamily: "monospace", color: T.gold, fontSize: 10, letterSpacing: 2,
};
const cardTitle = {
  color: T.text, fontSize: 16, fontWeight: 800, marginTop: 4,
};
const statSmall = {
  color: T.mute, fontFamily: "monospace", fontSize: 10, letterSpacing: 1,
};

// ──────────────────────────────────────────────────────────────────────
// WIDGET 1 — CRYPTO FEAR & GREED INDEX
// ──────────────────────────────────────────────────────────────────────
const FNG_URL = "https://api.alternative.me/fng/?limit=2";

function fngBucket(value) {
  const v = Number(value);
  if (v <= 25) return { label: "Extreme Fear", color: T.red };
  if (v <= 45) return { label: "Fear", color: T.orange };
  if (v <= 55) return { label: "Neutral", color: T.yellow };
  if (v <= 75) return { label: "Greed", color: T.teal };
  return { label: "Extreme Greed", color: T.green };
}

function FearGreedCard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchedAt, setFetchedAt] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(FNG_URL, { cache: "no-store" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const rows = json.data || [];
      if (rows.length === 0) throw new Error("Empty response");
      setData({
        today: rows[0],
        yesterday: rows[1] || null,
      });
      setError(null);
      setFetchedAt(new Date());
    } catch (e) {
      setError(e.message || "Fetch failed");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 10 * 60 * 1000); // every 10 min
    return () => clearInterval(t);
  }, [load]);

  const today = data?.today;
  const yest = data?.yesterday;
  const value = today ? Number(today.value) : 0;
  const bucket = today ? fngBucket(value) : { label: "—", color: T.mute };
  const delta = today && yest ? value - Number(yest.value) : null;

  return (
    <div data-testid="fng-card" style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <div>
          <div style={headerLabel}>😨 CRYPTO FEAR & GREED</div>
          <div style={cardTitle}>Market Sentiment</div>
        </div>
        <button data-testid="fng-refresh-btn" onClick={load} disabled={loading}
          style={{
            all: "unset", cursor: loading ? "wait" : "pointer",
            padding: "4px 10px", borderRadius: 4,
            border: `1px solid ${T.border}`, color: T.mute,
            fontFamily: "monospace", fontSize: 10, letterSpacing: 1,
          }}>{loading ? "⟳" : "↻ Refresh"}</button>
      </div>

      {error && (
        <div data-testid="fng-error" style={{
          marginTop: 12, padding: 10, borderRadius: 8,
          background: `${T.red}15`, border: `1px solid ${T.red}55`,
          color: T.red, fontSize: 12,
        }}>
          ⚠️ Feed offline — {error}
        </div>
      )}

      {today && (
        <>
          {/* Gauge */}
          <div data-testid="fng-gauge" style={{ marginTop: 14 }}>
            <FngGauge value={value} color={bucket.color} />
          </div>
          <div style={{ textAlign: "center", marginTop: 10 }}>
            <div data-testid="fng-value" style={{
              fontSize: 44, fontWeight: 900, lineHeight: 1,
              color: bucket.color, fontFamily: "'Bebas Neue','Oswald',monospace", letterSpacing: 1,
            }}>{value}</div>
            <div data-testid="fng-label" style={{
              color: bucket.color, fontFamily: "monospace", fontSize: 11,
              letterSpacing: 2, fontWeight: 800, marginTop: 4,
            }}>{bucket.label.toUpperCase()}</div>
          </div>

          <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <div data-testid="fng-yesterday" style={{ background: "#0a0e13", border: `1px solid ${T.border}`, borderRadius: 6, padding: 8, textAlign: "center" }}>
              <div style={statSmall}>YESTERDAY</div>
              <div style={{ color: T.text, fontFamily: "monospace", fontSize: 16, fontWeight: 700, marginTop: 2 }}>
                {yest ? yest.value : "—"}
              </div>
            </div>
            <div data-testid="fng-delta" style={{ background: "#0a0e13", border: `1px solid ${T.border}`, borderRadius: 6, padding: 8, textAlign: "center" }}>
              <div style={statSmall}>Δ DAILY</div>
              <div style={{
                color: delta == null ? T.mute : (delta > 0 ? T.green : delta < 0 ? T.red : T.mute),
                fontFamily: "monospace", fontSize: 16, fontWeight: 700, marginTop: 2,
              }}>
                {delta == null ? "—" : `${delta > 0 ? "+" : ""}${delta}`}
              </div>
            </div>
          </div>
          <div data-testid="fng-updated" style={{ marginTop: 10, textAlign: "center", ...statSmall }}>
            Updated {fetchedAt ? fetchedAt.toLocaleTimeString() : "—"} · Auto-refresh 10m
          </div>
        </>
      )}
    </div>
  );
}

// Semi-circle 0–100 gauge rendered as SVG — simple, no deps.
function FngGauge({ value, color }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  const angle = (v / 100) * 180 - 90; // -90..90
  const needleX = 100 + 80 * Math.sin((angle * Math.PI) / 180);
  const needleY = 100 - 80 * Math.cos((angle * Math.PI) / 180);
  return (
    <svg viewBox="0 0 200 115" width="100%" height="100" aria-hidden="true">
      <defs>
        <linearGradient id="fng-grad" x1="0" x2="1">
          <stop offset="0%" stopColor={T.red} />
          <stop offset="25%" stopColor={T.orange} />
          <stop offset="50%" stopColor={T.yellow} />
          <stop offset="75%" stopColor={T.teal} />
          <stop offset="100%" stopColor={T.green} />
        </linearGradient>
      </defs>
      <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke="url(#fng-grad)" strokeWidth="14" strokeLinecap="round" />
      {[0, 25, 50, 75, 100].map((t) => {
        const a = (t / 100) * 180 - 90;
        const x1 = 100 + 92 * Math.sin((a * Math.PI) / 180);
        const y1 = 100 - 92 * Math.cos((a * Math.PI) / 180);
        const x2 = 100 + 100 * Math.sin((a * Math.PI) / 180);
        const y2 = 100 - 100 * Math.cos((a * Math.PI) / 180);
        return <line key={t} x1={x1} y1={y1} x2={x2} y2={y2} stroke={T.mute} strokeWidth="1" />;
      })}
      <line x1="100" y1="100" x2={needleX} y2={needleY} stroke={color} strokeWidth="3" strokeLinecap="round" />
      <circle cx="100" cy="100" r="6" fill={color} />
    </svg>
  );
}

// ──────────────────────────────────────────────────────────────────────
// WIDGET 2 — GOLD (XAUUSD) DAILY PIVOTS  (auto-fetch + manual override)
// ──────────────────────────────────────────────────────────────────────
const PIVOTS_OVERRIDE_KEY = "flexx_gold_pivots_override";

function loadPivotsOverride() {
  try {
    const raw = localStorage.getItem(PIVOTS_OVERRIDE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) { return null; }
}

function computeClassicPivots(h, l, c) {
  const hi = Number(h), lo = Number(l), cl = Number(c);
  if (!(hi > 0) || !(lo > 0) || !(cl > 0)) return null;
  const p = (hi + lo + cl) / 3;
  const range = hi - lo;
  return {
    R3: hi + 2 * (p - lo),
    R2: p + range,
    R1: 2 * p - lo,
    P: p,
    S1: 2 * p - hi,
    S2: p - range,
    S3: lo - 2 * (hi - p),
  };
}

function GoldPivotsCard() {
  const [override, setOverride] = useState(() => loadPivotsOverride());
  const [auto, setAuto] = useState(null);       // backend OHLC
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);

  // The OHLC actually used for pivots: override wins if set, else auto.
  const ohlc = override || auto;
  const pivots = useMemo(() => (ohlc ? computeClassicPivots(ohlc.h, ohlc.l, ohlc.c) : null), [ohlc]);
  const source = override ? "manual" : (auto ? "twelvedata" : null);

  const loadAuto = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/market-intel/xauusd-ohlc`, { headers: authHeaders(), credentials: "include", cache: "no-store" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setAuto({
        o: Number(json.o), h: Number(json.h), l: Number(json.l), c: Number(json.c),
        date: json.date, fetched_at: json.fetched_at, cached: json.cached,
      });
      setError(null);
    } catch (e) {
      setError(e.message || "Fetch failed");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    loadAuto();
    const t = setInterval(loadAuto, 30 * 60 * 1000); // refresh every 30 min (matches backend cache)
    return () => clearInterval(t);
  }, [loadAuto]);

  const saveOverride = (next) => {
    setOverride(next);
    try { localStorage.setItem(PIVOTS_OVERRIDE_KEY, JSON.stringify(next)); } catch (e) { console.debug("[MarketIntel] override persist skipped:", e?.message || e); }
    setEditing(false);
  };

  const clearOverride = () => {
    setOverride(null);
    try { localStorage.removeItem(PIVOTS_OVERRIDE_KEY); } catch (e) { console.debug("[MarketIntel] override clear skipped:", e?.message || e); }
  };

  const rows = pivots ? [
    { key: "R3", value: pivots.R3, color: T.red, accent: "strong" },
    { key: "R2", value: pivots.R2, color: T.red, accent: "mid" },
    { key: "R1", value: pivots.R1, color: T.red, accent: "light" },
    { key: "P",  value: pivots.P,  color: T.gold, accent: "pivot" },
    { key: "S1", value: pivots.S1, color: T.green, accent: "light" },
    { key: "S2", value: pivots.S2, color: T.green, accent: "mid" },
    { key: "S3", value: pivots.S3, color: T.green, accent: "strong" },
  ] : [];

  return (
    <div data-testid="gold-pivots-card" style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <div>
          <div style={headerLabel}>🥇 XAUUSD PIVOTS</div>
          <div style={cardTitle}>Gold Daily Levels</div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button data-testid="gold-pivots-refresh-btn" onClick={loadAuto} disabled={loading}
            title="Re-fetch from TwelveData"
            style={{
              all: "unset", cursor: loading ? "wait" : "pointer",
              padding: "4px 10px", borderRadius: 4,
              border: `1px solid ${T.border}`, color: T.mute,
              fontFamily: "monospace", fontSize: 10, letterSpacing: 1,
            }}>{loading ? "⟳" : "↻"}</button>
          <button data-testid="gold-pivots-override-btn" onClick={() => setEditing(true)}
            style={{
              all: "unset", cursor: "pointer",
              padding: "4px 10px", borderRadius: 4,
              background: `${T.gold}15`, border: `1px solid ${T.gold}`, color: T.gold,
              fontFamily: "monospace", fontSize: 10, letterSpacing: 1, fontWeight: 800,
            }}>
            {override ? "✎ Edit Override" : "✎ Override"}
          </button>
        </div>
      </div>

      {loading && !ohlc && (
        <div data-testid="gold-pivots-loading" style={{
          marginTop: 14, padding: 14, border: `1px dashed ${T.border}`, borderRadius: 8,
          color: T.mute, fontSize: 12, textAlign: "center", fontStyle: "italic",
        }}>
          Fetching latest XAUUSD daily candle…
        </div>
      )}

      {!loading && !ohlc && error && (
        <div data-testid="gold-pivots-error" style={{
          marginTop: 12, padding: 10, borderRadius: 8,
          background: `${T.red}15`, border: `1px solid ${T.red}55`,
          color: T.red, fontSize: 12,
        }}>
          ⚠️ Auto-feed offline — {error}. Use ✎ Override to enter manually.
        </div>
      )}

      {pivots && (
        <>
          <div data-testid="gold-pivots-input-summary" style={{
            marginTop: 10, padding: 8, background: "#0a0e13", border: `1px solid ${T.border}`,
            borderRadius: 6, color: T.mute, fontFamily: "monospace", fontSize: 10, textAlign: "center", letterSpacing: 0.5,
          }}>
            O:{Number(ohlc.o).toFixed(2)} · H:{Number(ohlc.h).toFixed(2)} · L:{Number(ohlc.l).toFixed(2)} · C:{Number(ohlc.c).toFixed(2)}
            {ohlc.date ? ` · ${ohlc.date}` : ""}
          </div>
          <div data-testid="gold-pivots-ladder" style={{ marginTop: 12, display: "grid", gap: 3 }}>
            {rows.map((r) => <PivotRow key={r.key} row={r} />)}
          </div>
          <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <div data-testid="gold-pivots-source" style={statSmall}>
              {source === "manual"
                ? "🖊 Source: manual override"
                : `⛓ Source: TwelveData${auto?.cached ? " (cached)" : ""}`}
            </div>
            {override && (
              <button data-testid="gold-pivots-clear-override-btn" onClick={clearOverride}
                style={{
                  all: "unset", cursor: "pointer", padding: "3px 8px", borderRadius: 4,
                  color: T.mute, border: `1px solid ${T.border}`,
                  fontFamily: "monospace", fontSize: 10, letterSpacing: 1,
                }}>
                Clear override
              </button>
            )}
          </div>
        </>
      )}

      {editing && (
        <OhlcEditor initial={override || auto || {}} onSave={saveOverride} onClose={() => setEditing(false)} />
      )}
    </div>
  );
}

function PivotRow({ row }) {
  const isPivot = row.key === "P";
  const intensity = { strong: 0.22, mid: 0.15, light: 0.08, pivot: 0.22 }[row.accent] || 0.15;
  return (
    <div data-testid={`gold-pivots-row-${row.key}`} style={{
      display: "grid", gridTemplateColumns: "36px 1fr auto", gap: 10,
      padding: "6px 10px", borderRadius: 6,
      background: `rgba(${hexToRgb(row.color)}, ${intensity})`,
      border: `1px solid ${row.color}${isPivot ? "" : "55"}`,
      borderLeft: `3px solid ${row.color}`,
    }}>
      <span style={{
        color: row.color, fontFamily: "monospace", fontSize: 11,
        fontWeight: 800, letterSpacing: 1,
      }}>{row.key}</span>
      <span style={{ color: T.mute, fontSize: 10, fontFamily: "monospace", alignSelf: "center", letterSpacing: 0.5 }}>
        {isPivot ? "Pivot" : row.key.startsWith("R") ? "Resistance" : "Support"}
      </span>
      <span style={{
        color: isPivot ? T.gold : T.text,
        fontFamily: "monospace", fontSize: 13, fontWeight: 700,
      }}>{row.value.toFixed(2)}</span>
    </div>
  );
}

function hexToRgb(hex) {
  const h = hex.replace("#", "");
  const bigint = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  return `${(bigint >> 16) & 255}, ${(bigint >> 8) & 255}, ${bigint & 255}`;
}

function OhlcEditor({ initial, onSave, onClose }) {
  const [o, setO] = useState(initial.o || "");
  const [h, setH] = useState(initial.h || "");
  const [l, setL] = useState(initial.l || "");
  const [c, setC] = useState(initial.c || "");
  const [date, setDate] = useState(initial.date || new Date().toISOString().slice(0, 10));

  const canSave = [o, h, l, c].every((v) => Number(v) > 0) && Number(h) >= Number(l);

  return (
    <Portal>
      <div data-testid="gold-pivots-ohlc-modal" role="dialog"
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, zIndex: 11500,
          background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
        }}>
        <div onClick={(e) => e.stopPropagation()} style={{
          background: T.card, border: `1px solid ${T.gold}55`, borderRadius: 12,
          padding: 20, width: "min(420px, 100%)",
          boxShadow: `0 10px 40px rgba(201,162,39,0.22)`,
        }}>
          <div style={headerLabel}>🥇 MANUAL OVERRIDE</div>
          <div style={cardTitle}>Yesterday's XAUUSD Session</div>
          <p style={{ color: T.mute, fontSize: 12, marginTop: 6, lineHeight: 1.5 }}>
            Use this if TwelveData is down or you want to pin custom OHLC. Classic pivot formula uses H, L, C — O is stored for reference only.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
            <Field label="Open (O)"><input data-testid="ohlc-input-o" type="number" step="0.01" value={o} onChange={(e) => setO(e.target.value)} style={modalInput} /></Field>
            <Field label="High (H)"><input data-testid="ohlc-input-h" type="number" step="0.01" value={h} onChange={(e) => setH(e.target.value)} style={modalInput} /></Field>
            <Field label="Low (L)"><input data-testid="ohlc-input-l" type="number" step="0.01" value={l} onChange={(e) => setL(e.target.value)} style={modalInput} /></Field>
            <Field label="Close (C)"><input data-testid="ohlc-input-c" type="number" step="0.01" value={c} onChange={(e) => setC(e.target.value)} style={modalInput} /></Field>
          </div>
          <Field label="Session date">
            <input data-testid="ohlc-input-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} style={modalInput} />
          </Field>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
            <button data-testid="ohlc-cancel-btn" onClick={onClose} style={btnGhost}>Cancel</button>
            <button data-testid="ohlc-save-btn" disabled={!canSave}
              onClick={() => onSave({ o: Number(o), h: Number(h), l: Number(l), c: Number(c), date, saved_at: new Date().toISOString() })}
              style={{ ...btnPrimary, opacity: canSave ? 1 : 0.5, cursor: canSave ? "pointer" : "not-allowed" }}>
              Save Override
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block", marginTop: 8 }}>
      <div style={{ color: T.mute, fontSize: 10, letterSpacing: 1, textTransform: "uppercase", fontFamily: "monospace", marginBottom: 4 }}>{label}</div>
      {children}
    </label>
  );
}

const modalInput = {
  width: "100%", padding: "10px 12px", borderRadius: 8,
  background: "#13161c", border: `1px solid ${T.border}`,
  color: T.text, fontSize: 13, outline: "none", boxSizing: "border-box",
  fontFamily: "monospace",
};
const btnPrimary = {
  all: "unset", cursor: "pointer", padding: "8px 16px", borderRadius: 6,
  background: `linear-gradient(135deg, ${T.gold}, #E8B94F)`, color: "#0a0a0a",
  fontFamily: "monospace", fontSize: 11, letterSpacing: 1.5, fontWeight: 800,
};
const btnGhost = {
  all: "unset", cursor: "pointer", padding: "8px 14px", borderRadius: 6,
  background: "transparent", border: `1px solid ${T.border}`, color: T.mute,
  fontFamily: "monospace", fontSize: 11, letterSpacing: 1,
};

// ──────────────────────────────────────────────────────────────────────
// WIDGET 3 — DXY DIRECTION INDICATOR
// ──────────────────────────────────────────────────────────────────────
// Auto-fetches live DXY (US Dollar Index) from /api/market-intel/dxy
// (Yahoo Finance DX-Y.NYB upstream). Polls every 30 min to match backend
// cache. Renders directional bias (XAUUSD inverse) just like Fear/Greed.

function DxyDirectionCard() {
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/market-intel/dxy`, { headers: authHeaders(), credentials: "include", cache: "no-store" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setSnap(json);
      setError(null);
    } catch (e) {
      setError(e.message || "Fetch failed");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30 * 60 * 1000); // every 30 min
    return () => clearInterval(t);
  }, [load]);

  const current = snap?.current;
  const prev = snap?.previous_close;
  const delta = snap ? Number(snap.change) : null;
  const pct = snap ? Number(snap.change_pct) : null;
  const rising = delta != null && delta > 0;
  const flat = delta != null && Math.abs(delta) < 0.01;
  const color = delta == null ? T.mute : flat ? T.mute : rising ? T.green : T.red;
  const arrow = delta == null ? "·" : flat ? "→" : rising ? "↑" : "↓";
  const verdict = delta == null
    ? "Loading DXY snapshot…"
    : flat
      ? "DXY Flat → No Directional Edge"
      : rising
        ? "DXY Rising → Gold Bearish Pressure"
        : "DXY Falling → Gold Bullish Bias";

  return (
    <div data-testid="dxy-direction-card" style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <div>
          <div style={headerLabel}>💵 DXY DIRECTION</div>
          <div style={cardTitle}>US Dollar Index</div>
        </div>
        <button data-testid="dxy-refresh-btn" onClick={load} disabled={loading}
          style={{
            all: "unset", cursor: loading ? "wait" : "pointer",
            padding: "4px 10px", borderRadius: 4,
            border: `1px solid ${T.border}`, color: T.mute,
            fontFamily: "monospace", fontSize: 10, letterSpacing: 1,
          }}>{loading ? "⟳" : "↻"}</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 12 }}>
        <div data-testid="dxy-current-box" style={{
          padding: "10px 12px", borderRadius: 6,
          background: "#0a0e13", border: `1px solid ${T.border}`,
        }}>
          <div style={{ ...statSmall, marginBottom: 4 }}>CURRENT DXY</div>
          <div style={{
            fontFamily: "monospace", fontSize: 22, fontWeight: 800, color: T.text, lineHeight: 1.1,
          }}>{current != null ? Number(current).toFixed(2) : "—"}</div>
        </div>
        <div data-testid="dxy-prev-box" style={{
          padding: "10px 12px", borderRadius: 6,
          background: "#0a0e13", border: `1px solid ${T.border}`,
        }}>
          <div style={{ ...statSmall, marginBottom: 4 }}>PREVIOUS CLOSE</div>
          <div style={{
            fontFamily: "monospace", fontSize: 22, fontWeight: 800, color: T.mute, lineHeight: 1.1,
          }}>{prev != null ? Number(prev).toFixed(2) : "—"}</div>
        </div>
      </div>

      {error && !snap && (
        <div data-testid="dxy-error" style={{
          marginTop: 12, padding: 10, borderRadius: 8,
          background: `${T.gold}12`, border: `1px solid ${T.gold}55`,
          color: T.gold, fontSize: 12, lineHeight: 1.5,
        }}>
          ⚠ DXY feed refreshing — please check back in a moment.
        </div>
      )}

      {snap && (
        <>
          <div data-testid="dxy-verdict-row" style={{
            marginTop: 14, padding: 14, borderRadius: 10,
            background: `${color}12`, border: `1px solid ${color}`, textAlign: "center",
          }}>
            <div data-testid="dxy-arrow" style={{
              fontSize: 40, lineHeight: 1, color, fontWeight: 900,
            }}>{arrow}</div>
            <div data-testid="dxy-delta" style={{ color, fontFamily: "monospace", fontSize: 13, fontWeight: 700, marginTop: 4 }}>
              {delta > 0 ? "+" : ""}{Number(delta).toFixed(2)} ({pct > 0 ? "+" : ""}{Number(pct).toFixed(2)}%)
            </div>
            <div data-testid="dxy-verdict" style={{ color, fontFamily: "monospace", fontSize: 11, letterSpacing: 1.5, fontWeight: 800, marginTop: 6 }}>
              {verdict.toUpperCase()}
            </div>
          </div>
          <div data-testid="dxy-updated" style={{ marginTop: 10, textAlign: "center", ...statSmall }}>
            Updated {snap.fetched_at ? new Date(snap.fetched_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"} · Auto-refresh 30m
          </div>
        </>
      )}

      <div data-testid="dxy-note" style={{
        marginTop: 10, padding: 8, borderRadius: 6,
        background: "#0a0e13", border: `1px dashed ${T.border}`,
        color: T.mute, fontSize: 11, fontStyle: "italic", textAlign: "center",
      }}>
        💡 XAUUSD and DXY move inversely — use this as directional bias, not a signal.
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// WIDGET 4 — TODAY'S ECONOMIC CALENDAR (high + medium from FMP)
// ──────────────────────────────────────────────────────────────────────
function formatEventTime(iso) {
  if (!iso) return "";
  // FMP returns "YYYY-MM-DD HH:MM:SS" in UTC. Parse safely.
  const d = new Date(iso.replace(" ", "T") + "Z");
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function EconomicCalendarCard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMedium, setShowMedium] = useState(false);
  const [muted, setMuted] = useState(false);
  const [muteLoading, setMuteLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/market-intel/economic-calendar`, { headers: authHeaders(), credentials: "include", cache: "no-store" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
      setError(null);
    } catch (e) {
      setError(e.message || "Fetch failed");
    } finally { setLoading(false); }
  }, []);

  const loadMuteState = useCallback(async () => {
    try {
      const res = await fetch(`${API}/flexx/mute-econ-alerts/today`, { headers: authHeaders(), credentials: "include", cache: "no-store" });
      if (res.ok) { const j = await res.json(); setMuted(!!j.muted); }
    } catch (e) { console.debug("[MarketIntel] mute-state fetch skipped:", e?.message || e); /* unauth users just see unmuted */ }
  }, []);

  const toggleMute = useCallback(async () => {
    setMuteLoading(true);
    try {
      const method = muted ? "DELETE" : "POST";
      const res = await fetch(`${API}/flexx/mute-econ-alerts/today`, { method, headers: authHeaders(), credentials: "include" });
      if (res.ok) { const j = await res.json(); setMuted(!!j.muted); }
    } catch (e) { console.debug("[MarketIntel] toggleMute skipped:", e?.message || e); }
    finally { setMuteLoading(false); }
  }, [muted]);

  useEffect(() => {
    load();
    loadMuteState();
    const t = setInterval(load, 60 * 60 * 1000); // every 60 min
    return () => clearInterval(t);
  }, [load, loadMuteState]);

  const high = data?.high || [];
  const medium = data?.medium || [];

  return (
    <div data-testid="econ-calendar-card" style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <div>
          <div style={headerLabel}>📅 ECONOMIC CALENDAR</div>
          <div style={cardTitle}>Today's Key Events</div>
        </div>
        <button data-testid="econ-refresh-btn" onClick={load} disabled={loading}
          style={{
            all: "unset", cursor: loading ? "wait" : "pointer",
            padding: "4px 10px", borderRadius: 4,
            border: `1px solid ${T.border}`, color: T.mute,
            fontFamily: "monospace", fontSize: 10, letterSpacing: 1,
          }}>{loading ? "⟳" : "↻"}</button>
      </div>

      {loading && !data && (
        <div data-testid="econ-loading" style={{
          marginTop: 14, padding: 14, border: `1px dashed ${T.border}`, borderRadius: 8,
          color: T.mute, fontSize: 12, textAlign: "center", fontStyle: "italic",
        }}>
          Fetching today's calendar…
        </div>
      )}

      {error && (
        <div data-testid="econ-error" style={{
          marginTop: 12, padding: 10, borderRadius: 8,
          background: `${T.red}15`, border: `1px solid ${T.red}55`,
          color: T.red, fontSize: 12,
        }}>
          ⚠️ Feed offline — {error}
        </div>
      )}

      {/* Fallback / stale banners intentionally hidden from end users —
       * we don't want to advertise our backup data source. The `source`
       * field is still returned by the API for internal debugging. */}
      {data && data.stale && data.counts?.total === 0 && (
        <div data-testid="econ-stale-banner" style={{
          marginTop: 12, padding: 10, borderRadius: 8,
          background: `${T.gold}12`, border: `1px solid ${T.gold}55`,
          color: T.gold, fontSize: 12, lineHeight: 1.5,
        }}>
          ⚠ Calendar refreshing — please check back in a few minutes.
        </div>
      )}

      {data && (
        <>
          {/* RED BANNER: high-impact events */}
          {high.length > 0 ? (
            <div data-testid="econ-high-banner" style={{
              marginTop: 12, padding: 12, borderRadius: 10,
              background: `${T.red}12`, border: `1px solid ${T.red}`,
              borderLeft: `4px solid ${T.red}`,
            }}>
              <div style={{
                color: T.red, fontFamily: "monospace", fontSize: 11,
                letterSpacing: 2, fontWeight: 800, marginBottom: 8,
              }}>
                🚩 RED FLAG · {high.length} HIGH-IMPACT EVENT{high.length !== 1 ? "S" : ""}
              </div>
              <div data-testid="econ-high-list" style={{ display: "grid", gap: 6 }}>
                {high.map((ev, i) => (
                  <EventRow key={`h-${i}`} ev={ev} impact="high" />
                ))}
              </div>
            </div>
          ) : (
            <div data-testid="econ-no-high" style={{
              marginTop: 12, padding: 10, borderRadius: 8,
              background: `${T.green}10`, border: `1px solid ${T.green}55`,
              color: T.green, fontSize: 12, textAlign: "center", fontFamily: "monospace", letterSpacing: 1,
            }}>
              ✓ NO HIGH-IMPACT EVENTS TODAY
            </div>
          )}

          {/* COLLAPSED MEDIUM EVENTS */}
          {medium.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <button data-testid="econ-toggle-medium" onClick={() => setShowMedium((s) => !s)}
                style={{
                  all: "unset", cursor: "pointer", width: "100%", boxSizing: "border-box",
                  padding: "8px 10px", borderRadius: 6,
                  background: `${T.yellow}10`, border: `1px solid ${T.yellow}55`,
                  color: T.yellow, fontFamily: "monospace", fontSize: 11, letterSpacing: 1,
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                }}>
                <span>{showMedium ? "▾" : "▸"} More events ({medium.length} medium)</span>
                <span style={{ opacity: 0.7 }}>{showMedium ? "Hide" : "Show"}</span>
              </button>
              {showMedium && (
                <div data-testid="econ-medium-list" style={{ marginTop: 8, display: "grid", gap: 4 }}>
                  {medium.map((ev, i) => (
                    <EventRow key={`m-${ev.date || ""}-${ev.currency || ev.country || ""}-${ev.event || i}`} ev={ev} impact="medium" />
                  ))}
                </div>
              )}
            </div>
          )}

          <div data-testid="econ-updated" style={{ marginTop: 10, textAlign: "center", ...statSmall }}>
            {data.date} · {data.counts?.total || 0} total events · Auto-refresh 60m
          </div>

          <button data-testid="econ-mute-toggle" onClick={toggleMute} disabled={muteLoading}
            style={{
              all: "unset",
              marginTop: 10, width: "100%", padding: "8px 12px", borderRadius: 6,
              textAlign: "center", cursor: muteLoading ? "wait" : "pointer",
              background: muted ? `${T.yellow}18` : "transparent",
              border: `1px solid ${muted ? T.yellow : T.border}`,
              color: muted ? T.yellow : T.mute,
              fontFamily: "monospace", fontSize: 10, letterSpacing: 1.5,
            }}>
            {muted ? "🔕 PUSH ALERTS MUTED FOR TODAY · TAP TO UNMUTE" : "🔔 MUTE PUSH ALERTS FOR TODAY"}
          </button>
        </>
      )}
    </div>
  );
}

function EventRow({ ev, impact }) {
  const color = impact === "high" ? T.red : T.yellow;
  return (
    <div data-testid={`econ-event-${impact}`} style={{
      display: "grid", gridTemplateColumns: "50px 46px 1fr", gap: 8,
      padding: "6px 8px", borderRadius: 6,
      background: "#0a0e13", border: `1px solid ${T.border}`,
      alignItems: "center",
    }}>
      <span style={{ color: color, fontFamily: "monospace", fontSize: 10, fontWeight: 800, letterSpacing: 0.5 }}>
        {formatEventTime(ev.date)}
      </span>
      <span style={{
        color: T.text, fontFamily: "monospace", fontSize: 10, fontWeight: 700,
        textAlign: "center", padding: "1px 4px", background: `${color}18`,
        borderRadius: 3, border: `1px solid ${color}55`,
      }}>
        {ev.currency || ev.country || "—"}
      </span>
      <span style={{ color: T.text, fontSize: 11, lineHeight: 1.35 }}>
        {ev.event}
      </span>
    </div>
  );
}
